import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import moment from 'moment';

import { Button } from 'ui-components/components/button';
import { DatePicker } from 'ui-components/components/date-picker';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { ModalFrameworkMethodsPropType } from 'ui-components/prop-types/modal-framework-methods';

import { convertUserRequestedDate } from '../../../fulfillment-plans/convert-user-requested-date';
import { useSellerFormattedDate } from '../../../hooks/use-datetime-formats';

import Global from '../../../store/global-selectors';
import style from './hold-until-modal.module.less';
import { messages } from './messages';

export const HoldUntilModal = ({ modalFrameworkMethods, placeOrdersOnHold }) => {
  const { closeModal } = modalFrameworkMethods;
  const isLoading = useSelector(Global.selectHoldUntilIsLoading);
  const numberOfOrders = useSelector(Global.selectHoldUntilFulfillmentPlanIds).length;
  const [holdUntilDate, setHoldUntilDate] = useState(moment().startOf('day').add(1, 'd').toISOString());
  const userDateFormat = useSelector(Global.selectUserUserDateFormat);
  const formattedEntityDate = useSellerFormattedDate(holdUntilDate);
  const sellerTimeZone = useSelector(Global.selectSellerTimeZoneIANA);

  const updateHoldUntilDate = useCallback(
    (newDate) => {
      const date = moment(newDate, userDateFormat).toISOString();
      setHoldUntilDate(date);
    },
    [userDateFormat],
  );

  const saveHoldUntilDate = useCallback(() => {
    const convertedDate = convertUserRequestedDate(holdUntilDate, sellerTimeZone);
    placeOrdersOnHold(convertedDate);
  }, [holdUntilDate, placeOrdersOnHold, sellerTimeZone]);

  return (
    <Modal className={style['hold-until-modal']} size='small'>
      <ModalHeader closeModal={closeModal}>
        <FormattedMessage {...messages.modalTitle} />
      </ModalHeader>
      <ModalBody className={style['hold-until-modal-body']}>
        {isLoading && <LoadingIndicator className={style['loading-indicator']} />}
        {!isLoading && (
          <div className={style['hold-until-body-content']}>
            <div className={style['date-picker-wrapper']}>
              <FormattedMessage {...messages.instructionText} />
              <DatePicker dateFormat={userDateFormat} onChange={updateHoldUntilDate} value={formattedEntityDate} />
            </div>
            <div className={style['helper-text']}>
              <FormattedMessage {...messages.helperText} values={{ numberOfOrders }} />
            </div>
          </div>
        )}
      </ModalBody>
      {!isLoading && (
        <ModalFooter className={style['hold-until-modal-footer']}>
          <Button onClick={closeModal} theme='tertiary'>
            <FormattedMessage {...messages.cancelButton} />
          </Button>
          <Button className={style['hold-button']} onClick={saveHoldUntilDate} theme='primary'>
            <FormattedMessage {...messages.holdButton} />
          </Button>
        </ModalFooter>
      )}
    </Modal>
  );
};

HoldUntilModal.propTypes = {
  modalFrameworkMethods: ModalFrameworkMethodsPropType.isRequired,
  placeOrdersOnHold: PropTypes.func.isRequired,
};
